import { Card } from "@general-backoffice/core";
import { FormInput } from "@general-backoffice/core/index";
import moment from "moment";
import React, { useMemo } from "react";
import { Card as RSCard, CardBody, FormGroup } from "reactstrap";

import formatCurrency from "../../utils/formatCurrency";
import MonthlyPayoutsGraph from "./graphs/MonthlyPayoutsGraph";

const DEFAULT_CURRENCY = "EUR";

const STARTING_YEAR = 2023;
const FINAL_YEAR = parseInt(moment().format("YYYY"), 10);
const years: string[] = [];
for (let y = STARTING_YEAR; y <= FINAL_YEAR; y++) years.push(String(y));

/** Currency total record, e.g., { EUR: 100, USD: 50 } */
type CurrencyTotal = Record<string, number>;

/** Data for a single year from the API */
interface YearPayoutData {
  perMonth: Record<
    string,
    {
      total: CurrencyTotal;
      payouts: unknown[];
    }
  >;
  total: CurrencyTotal;
}

interface MonthlyPayoutsGraphCardProps {
  /** Year payout data containing perMonth breakdown and total */
  data?: YearPayoutData;
  isLoading?: boolean;
  year: string;
  setYear: (year: string) => void;
}

const MonthlyPayoutsGraphCard: React.FC<MonthlyPayoutsGraphCardProps> = ({ data, isLoading, year, setYear }) => {
  const { perMonth, total } = data || {};

  const sortedPerMonth = useMemo(() => {
    if (!data || !perMonth) return {};

    const numericYear = parseInt(year, 10);

    return Object.entries(perMonth).reduce<Record<string, number>>((r, [m, v], i, entries) => {
      const monthNum = parseInt(m, 10);

      // Add initial months if not there only on years that are not STARTING_YEAR
      if (numericYear !== STARTING_YEAR && !i && monthNum !== 1) {
        for (let j = 1; j < monthNum; j++) r[String(j)] = 0;
      }

      // Fill in any missing month
      if (i > 0) {
        const prevMonth = parseInt(Object.keys(perMonth)[i - 1], 10);
        for (let prevM = prevMonth + 1; prevM < monthNum; prevM++) r[String(prevM)] = 0;
      }

      r[m] = v?.total?.[DEFAULT_CURRENCY] ?? 0;

      // Add final months if not there only on years that are not FINAL_YEAR
      if (numericYear !== FINAL_YEAR && i === entries.length - 1) {
        for (let j = monthNum + 1; j <= 12; j++) r[String(j)] = 0;
      }

      return r;
    }, {});
  }, [data, perMonth, year]);

  const totalAmount = total?.[DEFAULT_CURRENCY] ?? 0;

  return (
    <RSCard>
      <Card.Header title="Monthly earnings" subtitle={`Total: ${formatCurrency(totalAmount, DEFAULT_CURRENCY)}`}>
        <FormGroup className="mb--4" style={{ minWidth: 120 }}>
          <FormInput.Select
            placeholder="Select a year"
            onChange={(opt: { value: string } | null) => opt && setYear(opt.value)}
            value={{ value: year, label: year }}
            options={years.map((y) => ({ value: y, label: y }))}
            isDisabled={isLoading}
            isClearable={false}
          />
        </FormGroup>
      </Card.Header>
      <CardBody className="pb-4">
        <MonthlyPayoutsGraph
          data={sortedPerMonth}
          isLoading={isLoading}
          className="rounded-bottom overflow-hidden"
          style={{ height: "calc(100vh - 400px)", minHeight: 400 }}
        />
      </CardBody>
    </RSCard>
  );
};

export default MonthlyPayoutsGraphCard;
